import { Column } from "@stenajs-webui/core";
import { Banner, SecondaryButton, stenaRefresh } from "@stenajs-webui/elements";
import { type FC, useState } from "react";
import { useDispatch } from "react-redux";

import { trackEvent } from "../../../../common/tracking/trackerService";
import { useBookingFormInstanceId } from "../../../../common/update-information/BookingFormInstanceIdProvider";
import type { ImportExportStatusCode } from "../../../../gql/graphql";
import { initializeAllUpdateFormsWithBooking } from "../../../../services/update-form/updateFormActionsHelpers";
import { useRefreshImportExportStatus } from "../../hooks/UseRefreshImportExportStatus";

interface Props {
  bookingNo: number;
  statusCode: ImportExportStatusCode;
}

export const ImportExportRefreshStatusButton: FC<Props> = ({
  bookingNo,
  statusCode,
}) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { refreshImportExportStatus, loading } = useRefreshImportExportStatus();

  const { formId } = useBookingFormInstanceId();
  const dispatch = useDispatch();

  const onClick = async () => {
    trackEvent({
      category: formId,
      action: "Get status update",
      label: statusCode,
    });
    setErrorMessage(null);

    try {
      const result = await refreshImportExportStatus(bookingNo);
      const errors = result.data?.refreshBookingImportExportStatus.errors;

      if ((errors && errors.length > 0) || result.error) {
        setErrorMessage("Failed to refresh status");
        trackEvent({ category: formId, action: "Failed to get status update" });
      } else {
        trackEvent({ category: formId, action: "Get status update success" });
        const booking = result.data?.refreshBookingImportExportStatus.booking;

        if (booking) {
          initializeAllUpdateFormsWithBooking(booking).forEach((action) =>
            dispatch(action),
          );
        }
      }
    } catch {
      setErrorMessage("Failed to refresh status");
      trackEvent({ category: formId, action: "Failed to get status update" });
    }
  };

  return (
    <Column gap={3}>
      <SecondaryButton
        loading={loading}
        leftIcon={stenaRefresh}
        label={"Get status update"}
        loadingLabel={"Get status update"}
        disabled={loading}
        onClick={onClick}
        className={"t_import_export_popup_button"}
      />
      {errorMessage && <Banner variant={"error"} text={errorMessage} />}
    </Column>
  );
};
